import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';

const OperationSelector = ({
  className,
  handleOnChange,
  options,
  title,
  value,
  type,
  disabled = false,
  rules = [],
  dependentFields = []
}) => {
  // Check if rules contain all required dependent field groups
  const checkDependentFields = () => {
    if (
      !rules ||
      rules.length === 0 ||
      !dependentFields ||
      dependentFields.length === 0
    ) {
      return false;
    }

    // Check if all field groups in dependentFields have at least one matching rule
    return dependentFields.every((fieldGroup) =>
      rules.some((rule) => fieldGroup.includes(rule.field))
    );
  };

  const shouldDisable = disabled || checkDependentFields();

  // Auto-change OR to AND when all dependent field groups exist
  useEffect(() => {
    if (checkDependentFields() && value === 'OR') {
      const andOption = options.find((option) => option.name === 'AND');
      if (andOption) {
        handleOnChange(andOption);
      }
    }
  }, [rules, value]);

  switch (type) {
    case 'classic':
      return (
        <StyledSelect
          className={`${className} ${shouldDisable ? 'opacity4' : ''}`}
          value={value}
          title={title}
          disabled={shouldDisable}
          onChange={(e) =>
            handleOnChange(options.find((el) => el.name === e.target.value))
          }
        >
          {options.map((option) => {
            const key = option.id ? `key-${option.id}` : `key-${option.name}`;
            return (
              <Option key={key} value={option.name}>
                {option.name}
              </Option>
            );
          })}
        </StyledSelect>
      );
    default:
      return (
        <div className="flex center">
          <div className="mr8">
            <FormattedMessage id="admin.slaPolicy.create.match" />
          </div>
          {options.map((option, idx) => {
            const key = option.id ? `key-${option.id}` : `key-${option.name}`;
            return (
              <Operator
                selected={option.name === value}
                value={value}
                disabled={shouldDisable}
                className={`${idx == 0 ? 'norightradius' : 'noleftradius'}`}
                onClick={(e) => !shouldDisable && handleOnChange(option)}
                key={key}
              >
                {option.label}
              </Operator>
            );
          })}
          <div className="ml8">
            <FormattedMessage id="admin.slaPolicy.create.conditions" />
          </div>
        </div>
      );
  }
};

const Operator = styled.div`
  background: ${(props) =>
    props.selected
      ? props.value === 'OR'
        ? '#E65D0F'
        : 'var(--sops-operation-selector-active-bg)'
      : '#EAEAEA'};
  color: ${(props) => (props.selected ? '#FFFFFF' : 'rgba(0,0,0,0.5)')};
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
`;

const StyledSelect = styled.select`
  background: ${(props) => (props.value === 'OR' ? '#E65D0F' : '#6C69FF')};
  font-weight: bold;
  color: #ffffff;
  font-size: 12px;
  padding: 2px;
  border-radius: 2px;
  text-transform: uppercase;
  margin-left: 20px;
`;

const Option = styled.option`
  font-size: 12px;
  padding: 2px;
  background: #ffffff;
  font-weight: bold;
  color: black;
  text-transform: uppercase;
  &hover {
    color: #ffffff;
    background: #ececec;
  }
`;

OperationSelector.displayName = 'OperationSelector';

OperationSelector.propTypes = {
  value: PropTypes.string,
  options: PropTypes.array.isRequired,
  className: PropTypes.string,
  handleOnChange: PropTypes.func,
  title: PropTypes.string,
  rules: PropTypes.array,
  dependentFields: PropTypes.arrayOf(PropTypes.array)
};

export default OperationSelector;
